import { useEffect } from "react";
import Stack from "@mui/material/Stack";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getSupplierList } from "app/redux/slice/supplierSlice";

const AddCoupon = ({ value, onClose, handleSubmit }) => {
  const dispatch = useDispatch();
  const supplierData = useSelector((state) => state?.supplier);

  useEffect(() => {
    dispatch(getSupplierList({ sort: 1, field: "name" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = Yup.object({
    code: Yup.string()
      .required("Please enter coupon code")
      .max(30, "Coupon code must not exceed 30 characters"),
    title: Yup.string().nullable().max(100, "Title must not exceed 100 characters"),
    description: Yup.string().nullable().max(500, "Description must not exceed 500 characters"),
    discountType: Yup.string().required("Please select discount type"),
    discountValue: Yup.number()
      .typeError("Discount value must be a number")
      .required("Please enter discount value")
      .min(0, "Discount value cannot be negative"),
    minCartValue: Yup.number()
      .typeError("Minimum cart value must be a number")
      .min(0, "Minimum cart value cannot be negative"),
    maxDiscountAmount: Yup.number()
      .nullable()
      .typeError("Max discount amount must be a number")
      .min(0, "Max discount amount cannot be negative"),
    usageLimit: Yup.number()
      .nullable()
      .typeError("Usage limit must be a number")
      .min(0, "Usage limit cannot be negative"),
  });
  const mobileView = useMediaQuery("(max-width:575px)");

  return (
    <>
      <Formik
        initialValues={{
          code: value?.code || "",
          title: value?.title || "",
          description: value?.description || "",
          discountType: value?.discountType || "percentage",
          discountValue: value?.discountValue ?? "",
          minCartValue: value?.minCartValue ?? 0,
          maxDiscountAmount: value?.maxDiscountAmount ?? "",
          validFrom: value?.validFrom
            ? new Date(value.validFrom).toISOString().slice(0, 10)
            : "",
          expiryDate: value?.expiryDate
            ? new Date(value.expiryDate).toISOString().slice(0, 10)
            : "",
          usageLimit: value?.usageLimit ?? "",
          assignedSuppliers:
            value?.assignedSuppliers
              ?.map((id) =>
                (supplierData?.list ?? []).find((s) => s._id === id),
              )
              .filter(Boolean) ?? [],
        }}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={(data, { setSubmitting }) => {
          setSubmitting(true);
          setTimeout(() => {
            handleSubmit(data);
            setSubmitting(false);
          }, 1000);
        }}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => (
          <Form>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField fullWidth name="code" label="Coupon Code*" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField fullWidth name="title" label="Title" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  error={touched.discountType && Boolean(errors.discountType)}
                >
                  <InputLabel>Discount Type*</InputLabel>
                  <Select
                    name="discountType"
                    value={values.discountType}
                    label="Discount Type*"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="percentage">Percentage (%)</MenuItem>
                    <MenuItem value="flat">Flat Amount</MenuItem>
                  </Select>
                  {touched.discountType && errors.discountType && (
                    <FormHelperText>{errors.discountType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField
                  fullWidth
                  name="discountValue"
                  label="Discount Value*"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField
                  fullWidth
                  name="minCartValue"
                  label="Minimum Cart Value"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField
                  fullWidth
                  name="maxDiscountAmount"
                  label="Max Discount Amount (for %)"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField
                  fullWidth
                  name="usageLimit"
                  label="Usage Limit"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField
                  fullWidth
                  name="validFrom"
                  label="Valid From"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField
                  fullWidth
                  name="expiryDate"
                  label="Valid To (Expiry Date)"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <JumboAppTextField
                  fullWidth
                  multiline
                  rows={2}
                  name="description"
                  label="Description"
                />
              </Grid>
              <Grid item xs={12}>
                <JumboAutoComplete
                  isMulti
                  options={supplierData?.list ?? []}
                  label="Assign to Suppliers (leave empty for Global)"
                  getOptionLabel={(option) => option?.name || ""}
                  value={values.assignedSuppliers}
                  name="assignedSuppliers"
                  onChange={(evt, value) => {
                    setFieldValue("assignedSuppliers", value);
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {values.assignedSuppliers?.length
                    ? `Visible only to the ${values.assignedSuppliers.length} selected supplier(s).`
                    : "No suppliers selected - this coupon will be Global (visible to all approved suppliers)."}
                </Typography>
              </Grid>
            </Grid>
            <Stack
              spacing={{ xs: 1, sm: 2 }}
              direction="row"
              justifyContent="flex-end"
              sx={{ mt: 3 }}
            >
              <LoadingButton
                variant="outlined"
                color="secondary"
                onClick={onClose}
                size="large"
                fullWidth={mobileView}
              >
                Cancel
              </LoadingButton>
              <LoadingButton
                type="submit"
                variant="contained"
                size="large"
                loading={isSubmitting}
                loadingIndicator={<CircularProgress size={20} />}
                fullWidth={mobileView}
              >
                Save
              </LoadingButton>
            </Stack>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddCoupon;
